import { useContext, useState } from 'react';

import { logger } from 'appSelectorContext';
import { keyBy } from 'lodash';

import { useGetUserInformation, useMetrics } from '../../appSelectorContext';
import { METRIC_PUBLISHER_NAMES } from '../../constants';
import { Context, Types } from '../../store';
import { AppContext, AppType } from '../../store/appContext';
import {
  AsyncState,
  GetReceiveStatusByExecutionIdResponse,
  InventoryReceivedStatus,
  Model,
  ReceivedLineItemExecutionStatus,
  SerialInfoStatus,
} from '../../store/types';
import { useService } from '../../store/useService';
import { EXECUTION_WAIT_TIME } from '../Utils/Utils';
import { GET_EXECUTION_STATUS } from './GetExecutionStatusService';
import { SERIALS_RECEIVED_ERRORED, SERIALS_RECEIVED_PROCESSED, SERIALS_RECEIVED_SUCCEEDED } from './constants';
import { getSerialList } from './util';

interface Props {
  onCompleted?: () => void;
}

const normalizeInventoryReceivedStatusToAsyncState = (status: InventoryReceivedStatus) => {
  switch (status) {
    case InventoryReceivedStatus.OK:
      return AsyncState.OK;
    case InventoryReceivedStatus.IN_PROGRESS:
      return AsyncState.IN_PROGRESS;
    case InventoryReceivedStatus.ERRORED:
      return AsyncState.ERROR;
    default:
      return status;
  }
};

/**
 * The useExecutionStatus hook provides data and business logic for the ExecutionStatus component

 */
export const useExecutionStatus = ({ onCompleted }: Props) => {
  const { dispatch, state: globalState }: any = useContext(Context);
  const [state, setState] = useState(AsyncState.IN_PROGRESS);
  const { useLazyQuery } = useService();
  const { initialMetricsPublisher } = useMetrics();
  const { user } = useGetUserInformation();
  const appContext = useContext(AppContext);

  const publisher = initialMetricsPublisher.newChildActionPublisherForMethod(METRIC_PUBLISHER_NAMES.SERIALS_RECEIVED);

  const [executionStatus, { data, loading, error, variables, stopPolling, called }] = useLazyQuery(
    GET_EXECUTION_STATUS,
    {
      fetchPolicy: 'network-only',
      pollInterval: 3000,
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        const execution: GetReceiveStatusByExecutionIdResponse = data?.getReceiveStatusByExecutionId;
        const reqVariables = appContext.appType === AppType.BOOST ? execution?.variables : variables;
        const executionReceiveStatus: AsyncState = normalizeInventoryReceivedStatusToAsyncState(
          execution.inventoryReceiveStatus,
        );

        const lineItem: ReceivedLineItemExecutionStatus =
          data?.getReceiveStatusByExecutionId?.receivedLineItemExecutionStatusList?.[0];

        const model = globalState?.models?.filter(
          (m: Model) =>
            m.referenceLineItemId === lineItem.referenceLineItemId &&
            m.status === AsyncState.IN_PROGRESS &&
            m.receiveExecutionUUId === variables?.receiveExecutionUuId,
        );

        //if model is deleted and execution is still in progress, stop polling
        if (!model) {
          stopPolling();
          return;
        }

        const totalSerials = lineItem?.containerizedMaterialStatusList.reduce((total: number, curr: any) => {
          const currentQty = curr?.receivedSerialList.length;
          return total + currentQty;
        }, 0);

        const settledSerialsList = getSerialList(
          lineItem,
          (s) => !![InventoryReceivedStatus.OK, InventoryReceivedStatus.ERRORED].includes(s.serialReceivedStatus),
        );

        const settledSerialsListWithStatus = settledSerialsList?.map((s: SerialInfoStatus) => {
          const error = s.serialReceivedStatus === InventoryReceivedStatus.ERRORED;
          const succeeded = s.serialReceivedStatus === InventoryReceivedStatus.OK;
          let receiveState = AsyncState.IN_PROGRESS;
          if (error) {
            receiveState = AsyncState.ERROR;
          }
          if (succeeded) {
            receiveState = AsyncState.OK;
          }
          return { ...s, receiveState };
        });

        const settledSerialsMap = keyBy(settledSerialsListWithStatus, 'externalSerialId');
        const allSettled = [AsyncState.OK, AsyncState.ERROR].includes(executionReceiveStatus);

        let receivedErrorsList = [];

        const currentTime: number = Date.now();
        const receiveSubmissionTime = execution?.receiveSubmissionTime ? execution?.receiveSubmissionTime : '0';
        const submittedTime: number = parseInt(receiveSubmissionTime);
        const waitTime = Math.floor((currentTime - submittedTime) / 1000);
        // if execution is not processing with in the wait time, cancel the stop polling
        const executionPastWaitTime = submittedTime > 0 && waitTime > EXECUTION_WAIT_TIME && !allSettled;
        /* istanbul ignore next */
        if (executionPastWaitTime) {
          stopPolling(reqVariables);
          return;
        }

        if (allSettled) {
          stopPolling(reqVariables);

          const succeeded = settledSerialsListWithStatus.filter((s) => s.receiveState === AsyncState.OK);
          const failed = settledSerialsListWithStatus.filter((s) => s.receiveState === AsyncState.ERROR);

          publisher.publishCounterMonitor(SERIALS_RECEIVED_PROCESSED, totalSerials);
          publisher.publishCounterMonitor(SERIALS_RECEIVED_SUCCEEDED, succeeded.length);
          publisher.publishCounterMonitor(SERIALS_RECEIVED_ERRORED, failed.length);

          // fetch all the errors till containers and consolidate, serials errors will be shown at serials level
          execution.errorCode && receivedErrorsList.push(execution.errorCode);
          lineItem.errorCode && receivedErrorsList.push(lineItem.errorCode);
          // container errors
          lineItem.containerizedMaterialStatusList.forEach((c) => {
            c.errorCode &&
              receivedErrorsList.push({
                id: c.containerScannableId,
                errorCode: c.errorCode,
              });
          });
        }
        const receiveExecutionUUId = reqVariables?.receiveExecutionUuId;

        logger.info('receiveExecutionUuId', receiveExecutionUUId);

        const payload = {
          receiveExecutionUUId,
          totalSerials,
          status: executionReceiveStatus,
          data: data?.getReceiveStatusByExecutionId,
          serialsProcessed: settledSerialsMap,
          receivedErrorsList,
        };

        logger.info(
          `updating receivePartsInModel : ${JSON.stringify({
            payload,
            withUser: user,
          })} `,
        );

        state !== executionReceiveStatus && setState(executionReceiveStatus);
        dispatch({
          type: Types.receivePartsInModel,
          payload,
        });
        onCompleted && onCompleted();
      },
    },
  );
  return {
    executionStatus,
    executionStatusResponse: data?.getReceiveStatusByExecutionId,
    stopPolling,
    loading,
    error,
    called,
    state,
  };
};

export default useExecutionStatus;
